import {ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {FormGroup, Validators, ReactiveFormsModule, FormControl} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';

import {TranslateModule} from '@ngx-translate/core';
import {finalize} from 'rxjs/operators';
import {SessionService} from '../session/session.service';
import { FooterComponent } from "../shared/footer/footer.component";
import { CustomerService } from '../core/customer.service';
import { MatIconModule } from '@angular/material/icon';
import { UserType } from 'sp-client';
import { VirtualKeyboardComponent } from '../shared/virtual-keyboard/virtual-keyboard.component';

@Component({
  selector: 'sp-login',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TranslateModule,
    FooterComponent,
    MatIconModule,
    VirtualKeyboardComponent
],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private customerService = inject(CustomerService);

  @ViewChild('emailInput') emailInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput') passwordInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild(VirtualKeyboardComponent) keyboard!: VirtualKeyboardComponent;

  protected readonly loginForm = new FormGroup({
    email: new FormControl('', {validators: [Validators.required, Validators.email], nonNullable: true}),
    password: new FormControl('', {validators: [Validators.required], nonNullable: true}),
  });

  protected readonly customerName = computed(() => {
    if (this.customerService.clients.status() === 'error') return undefined;
    return this.customerService.clients.value()?.[0]?.company;
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  // Virtual keyboard state
  protected readonly showKeyboard = signal(false);
  protected readonly activeField = signal<'email' | 'password' | null>(null);
  protected readonly keyboardValue = signal('');
  protected readonly activeInputElement = signal<HTMLInputElement | null>(null);

  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.loginForm.disable();
    this.showKeyboard.set(false);

    const { email, password } = this.loginForm.getRawValue();

    this.sessionService.login(email, password)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.loginForm.enable();
        })
      )
      .subscribe({
        next: () => {
          const currentUser = this.sessionService.user();
          if (currentUser?.user_type_id === UserType.SuperAdmin) {
            void this.router.navigate(['/super-admin']);
          } else {
            void this.router.navigate(['/']);
          }
        },
        error: (err) => {
          this.errorMessage.set(err?.message ?? 'login.errorMessage');
          console.error('Login error:', err);
        }
      });
  }

  // Toggle keyboard visibility
  toggleKeyboard(): void {
    if (this.showKeyboard()) {
      this.onKeyboardClose();
    } else {
      // Open keyboard - if no field is active, default to email
      const field = this.activeField() ?? 'email';
      this.switchToField(field);
      this.showKeyboard.set(true);
    }
  }

  // Called when field receives focus (tab, click, etc.)
  onFieldFocus(field: 'email' | 'password'): void {
    this.switchToField(field);
  }

  // Called when field is clicked
  onFieldClick(field: 'email' | 'password'): void {
    this.switchToField(field);
    // Sync cursor position after click
    setTimeout(() => {
      const inputEl = this.getInputElement(field);
      if (inputEl && this.keyboard) {
        this.keyboard.syncCaretPosition(inputEl);
      }
    }, 10);
  }

  // Called when user types with physical keyboard
  onFieldInput(field: 'email' | 'password'): void {
    if (this.activeField() === field) {
      const control = this.loginForm.get(field);
      this.keyboardValue.set(control?.value ?? '');
    }
  }

  private switchToField(field: 'email' | 'password'): void {
    this.activeField.set(field);
    const control = this.loginForm.get(field);
    this.keyboardValue.set(control?.value ?? '');

    const inputEl = this.getInputElement(field);
    this.activeInputElement.set(inputEl);
  }

  private getInputElement(field: 'email' | 'password'): HTMLInputElement | null {
    if (field === 'email') {
      return this.emailInputRef?.nativeElement ?? null;
    } else {
      return this.passwordInputRef?.nativeElement ?? null;
    }
  }

  onKeyboardValueChange(value: string): void {
    const field = this.activeField();
    if (field) {
      this.loginForm.get(field)?.setValue(value);
      this.keyboardValue.set(value);
    }
  }

  onKeyboardEnter(): void {
    const field = this.activeField();
    if (field === 'email') {
      // Move to password field
      this.switchToField('password');
      this.getInputElement('password')?.focus();
    } else if (field === 'password') {
      // Submit the form
      this.showKeyboard.set(false);
      this.onSubmit();
    }
  }

  onKeyboardClose(): void {
    this.showKeyboard.set(false);
  }
}
